/**
 * Lead offer: the store's own welcome-discount capture (#294), replacing
 * Omnisend's popup. Markup and server-side facts come from
 * snippets/lead-offer.liquid; this file decides WHEN to show it and runs it.
 *
 * Two halves:
 *   - LeadOfferRules: pure functions (no DOM, no storage), unit-tested in
 *     tests/lead-offer.test.mjs by loading this file in a VM.
 *   - the controller at the bottom: reads/writes state, listens for triggers,
 *     opens the <dialog>, reports analytics.
 *
 * Capture is a NATIVE form post. Shopify requires an h-captcha-response
 * token on customer forms and only issues one during a native submit; a
 * fetch() gets `400 Missing CAPTCHA token` (#298). So the page reloads after
 * signup and the success state (with the code) is rendered server-side from
 * form.posted_successfully?; this file only reopens the dialog on it.
 */
(function () {
  'use strict';

  var DAY = 24 * 60 * 60 * 1000;

  var LeadOfferRules = {
    /**
     * Should the offer open by itself right now?
     *
     * env:   { enabled, forced, serverSuppressed, autoOpenAllowed }
     * state: { subscribed, closedAt, shownThisSession, dismissedThisSession,
     *          addedToCartThisSession, fromEmail, pageviews }
     * page:  { now, seconds, scroll (0..1), exitIntent, desktop }
     * cfg:   { minSeconds, engagedPageviews, engagedSeconds, engagedScroll,
     *          exitIntentSeconds, cooldownDays }
     *
     * Returns { open: boolean, reason: string }. The reason is for debugging
     * and analytics; it names the first rule that decided.
     */
    decide: function (env, state, page, cfg) {
      if (!env.enabled && !env.forced) return { open: false, reason: 'disabled' };
      if (env.serverSuppressed) return { open: false, reason: 'server-suppressed' };
      if (state.subscribed) return { open: false, reason: 'subscribed' };
      if (env.forced) {
        return page.seconds >= 1 ? { open: true, reason: 'forced' } : { open: false, reason: 'forced-wait' };
      }
      if (!env.autoOpenAllowed) return { open: false, reason: 'template' };
      if (state.shownThisSession) return { open: false, reason: 'shown-this-session' };
      if (state.dismissedThisSession) return { open: false, reason: 'dismissed-this-session' };
      if (state.addedToCartThisSession) return { open: false, reason: 'added-to-cart' };
      if (state.fromEmail) return { open: false, reason: 'from-email' };
      if (state.closedAt && page.now - state.closedAt < cfg.cooldownDays * DAY) {
        return { open: false, reason: 'cooldown' };
      }
      if (page.seconds < cfg.minSeconds) return { open: false, reason: 'too-early' };
      if (LeadOfferRules.engaged(state, page, cfg)) return { open: true, reason: 'engaged' };
      if (page.desktop && page.exitIntent && page.seconds >= cfg.exitIntentSeconds) {
        return { open: true, reason: 'exit-intent' };
      }
      return { open: false, reason: 'waiting' };
    },

    /** A second page this session, or real time + scroll on this one. */
    engaged: function (state, page, cfg) {
      if (state.pageviews >= cfg.engagedPageviews) return true;
      return page.seconds >= cfg.engagedSeconds && page.scroll >= cfg.engagedScroll;
    },

    /** Contact tags for the Shopify customer, e.g. "newsletter, welcome_discount, …". */
    tags: function (placement) {
      var p = String(placement || 'auto').toLowerCase().replace(/[^a-z0-9_-]/g, '');
      return ['newsletter', 'welcome_discount', 'source:native_offer', 'placement:' + (p || 'auto')].join(', ');
    },

    /**
     * After the native post reloads the page: was it OUR form, and did it work?
     * `pending` is what we stored at submit; `posted` is ?customer_posted=true;
     * `hasErrors` is whether the offer form rendered errors.
     */
    afterReload: function (pending, posted, hasErrors, now) {
      if (!pending || !pending.at || now - pending.at > 30 * 60 * 1000) return 'none';
      if (posted) return 'success';
      if (hasErrors) return 'error';
      return 'none';
    },

    /**
     * Cart drawer line: only for a cart with exactly ONE item and no code
     * applied. With 2+ items the automatic Bundle & Save tier applies and the
     * welcome code is not honoured (DISCOUNT_CODE_NOT_HONOURED), so offering
     * it there would be a false promise.
     */
    drawerEligible: function (cart) {
      if (!cart || cart.item_count !== 1) return false;
      if ((cart.discount_codes || []).length) return false;
      // Any other discount already on the cart (automatic, app, line-level)
      // won't combine with the welcome code either.
      if ((cart.cart_level_discount_applications || []).length) return false;
      return !(cart.items || []).some(function (i) {
        return (i.line_level_discount_allocations || []).length || (i.discounts || []).length;
      });
    },

    /** utm_medium=email (Shopify Email / Messaging / Omnisend links). */
    fromEmail: function (search) {
      return /(?:^|[?&])utm_medium=email(?:&|$)/i.test(search || '');
    }
  };

  window.LeadOfferRules = LeadOfferRules;

  // ---------------------------------------------------------------------------
  // Controller
  // ---------------------------------------------------------------------------
  var LS_KEY = 'lp_offer';
  var SS_KEY = 'lp_offer_s';

  function read(store, key) {
    try { return JSON.parse(window[store].getItem(key) || '{}') || {}; } catch (e) { return {}; }
  }
  function write(store, key, value) {
    try { window[store].setItem(key, JSON.stringify(value)); } catch (e) { /* storage blocked: fine */ }
  }

  function track(name, data) {
    var payload = Object.assign({ event: 'lp_offer_' + name }, data || {});
    try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (e) { /* noop */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('lp_offer_' + name, data || {});
      }
    } catch (e) { /* noop */ }
  }

  function init() {
    // Shopify's /challenge page (captcha fallback for a form post) renders the
    // theme too; the offer must never sit on top of that checkbox.
    if (window.location.pathname === '/challenge') return;
    var root = document.getElementById('lead-offer');
    var configEl = document.getElementById('lead-offer-config');
    if (!root || !configEl) return;
    var cfg;
    try { cfg = JSON.parse(configEl.textContent); } catch (e) { return; }

    var dialog = root.querySelector('dialog');
    var form = root.querySelector('form');
    var tagsInput = root.querySelector('[data-lead-offer-tags]');
    if (!dialog || !form) return;

    var search = window.location.search || '';
    var param = (search.match(/[?&]lp_offer=([a-z]+)/) || [])[1];
    var session = read('sessionStorage', SS_KEY);
    var local = read('localStorage', LS_KEY);

    if (param === 'reset') {
      session = {}; local = {};
      write('sessionStorage', SS_KEY, session); write('localStorage', LS_KEY, local);
    }
    if (param === 'on') session.forced = true;
    // `rules`: behave exactly as if enabled (all trigger rules apply) for this
    // session only, to verify cutover behaviour before flipping lo_enabled.
    if (param === 'rules') session.simulateEnabled = true;
    if (param === 'off') { session.forced = false; session.simulateEnabled = false; }

    session.pageviews = (session.pageviews || 0) + 1;
    if (LeadOfferRules.fromEmail(search)) session.fromEmail = true;

    // Any newsletter signup (footer, section, this form) means: stop asking.
    // Only trust the flag after a real round-trip from this store's form (the
    // redirect keeps a same-origin referrer); a pasted/shared link carrying
    // ?customer_posted=true must not switch the offer off for that visitor.
    var posted = /[?&]customer_posted=true/.test(search);
    var sameOriginReferrer = false;
    try { sameOriginReferrer = !!document.referrer && new URL(document.referrer).host === window.location.host; } catch (e) { /* noop */ }
    if (posted && sameOriginReferrer) local.subscribed = true;

    write('sessionStorage', SS_KEY, session);
    write('localStorage', LS_KEY, local);

    var placement = 'auto';
    var start = Date.now();
    var maxScroll = 0;
    var exitIntent = false;
    var desktop = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // Shopify's captcha (storefront-forms-hcaptcha) only starts loading on the form's first
    // focusin. A shopper who autofills and taps at once beats it, and the first submit stalls.
    // A synthetic focusin when the dialog opens warms it up without moving focus, so the phone
    // keyboard stays down.
    function warmCaptcha() {
      var input = form.querySelector('input[type="email"]');
      if (!input || form.getAttribute('data-captcha-warmed')) return;
      form.setAttribute('data-captcha-warmed', '1');
      try { input.dispatchEvent(new FocusEvent('focusin', { bubbles: true })); } catch (e) { /* noop */ }
    }

    var openY = null;
    function show(reason, how) {
      if (dialog.open) return;
      openY = window.scrollY;
      if (tagsInput) tagsInput.value = LeadOfferRules.tags(placement);
      root.hidden = false;
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
      warmCaptcha();
      var s = read('sessionStorage', SS_KEY);
      s.shownThisSession = true;
      write('sessionStorage', SS_KEY, s);
      track('view', { placement: placement, reason: reason, state: how || 'form' });
    }

    function close() {
      if (!dialog.open) return;
      if (typeof dialog.close === 'function') dialog.close(); else dialog.removeAttribute('open');
    }

    dialog.addEventListener('close', function () {
      if (swapping) { swapping = false; return; }
      root.classList.remove('lead-offer--inline');
      root.hidden = true;
      var inSuccess = root.getAttribute('data-state') === 'success';
      if (!inSuccess) {
        var l = read('localStorage', LS_KEY);
        l.closedAt = Date.now();
        write('localStorage', LS_KEY, l);
        var s = read('sessionStorage', SS_KEY);
        s.dismissedThisSession = true;
        write('sessionStorage', SS_KEY, s);
        track('dismiss', { placement: placement });
      }
    });
    // Click on the backdrop (the dialog element itself, outside the panel) closes.
    dialog.addEventListener('click', function (e) { if (e.target === dialog) close(); });
    root.querySelectorAll('[data-lead-offer-close]').forEach(function (b) {
      b.addEventListener('click', function () { close(); });
    });

    // A modal <dialog> sits in the top layer and makes the rest of the page
    // inert. If hCaptcha escalates to a visible puzzle it appends that to
    // <body> — behind the modal and unclickable — so the shopper would be stuck.
    // On submit, re-show the dialog non-modally (own backdrop via CSS) so a
    // puzzle can appear above it. The close this triggers is not a dismissal.
    var swapping = false;
    function makeNonModal() {
      var modal = false;
      try { modal = dialog.matches(':modal'); } catch (e) { modal = false; }
      if (!modal || typeof dialog.show !== 'function') return;
      swapping = true;
      dialog.close();
      dialog.show();
      root.classList.add('lead-offer--inline');
    }

    form.addEventListener('submit', function () {
      makeNonModal();
      // Use where the shopper was when the dialog opened: Shopify's captcha scrolls the page to
      // the form (end of <body>) and re-submits, so scrollY here can already be the footer.
      write('sessionStorage', 'lp_offer_pending', { at: Date.now(), placement: placement, y: openY != null ? openY : window.scrollY });
      root.setAttribute('data-state', 'submitting');
      track('submit', { placement: placement });
      // Shopify's challenge can stall on a first attempt (hcaptcha.execute never
      // resolves; a second tap goes through), so don't spin forever (#298).
      window.setTimeout(function () {
        if (root.getAttribute('data-state') === 'submitting') root.setAttribute('data-state', 'form');
      }, 15000);
    });

    var copyBtn = root.querySelector('[data-lead-offer-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var code = copyBtn.getAttribute('data-code') || '';
        var done = function () {
          copyBtn.textContent = copyBtn.getAttribute('data-copied-text') || copyBtn.textContent;
          track('code_copy', { placement: placement });
        };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done, done);
        else done();
      });
    }

    // Surfaces (sticky bar strip, cart drawer line, header link) call this.
    window.LeadOffer = {
      open: function (where) {
        // Surfaces must not bypass the master switch: while lo_enabled is false
        // only a QA session (?lp_offer=on|rules) may open it.
        if (!window.LeadOffer.available()) return false;
        placement = where || 'surface';
        root.setAttribute('data-state', 'form');
        track('open', { placement: placement });
        show('surface');
        return true;
      },
      available: function () { var s = read('sessionStorage', SS_KEY); return !cfg.serverSuppressed && !read('localStorage', LS_KEY).subscribed && (cfg.enabled || !!s.forced || !!s.simulateEnabled); }
    };
    document.dispatchEvent(new CustomEvent('lead-offer:ready'));

    // -------------------------------------------------------------------------
    // Surfaces (#294 PR 2): docked entry points that open the dialog. Markup is
    // <template>s in snippets/lead-offer.liquid (single source for copy and
    // percent); they are inserted ONLY while the offer is available, so with
    // lo_enabled=false nothing is added to the sticky bar, drawer or ticker.
    // -------------------------------------------------------------------------
    function fromTemplate(id) {
      var tpl = document.getElementById(id);
      return tpl && tpl.content && tpl.content.firstElementChild ? tpl.content.firstElementChild.cloneNode(true) : null;
    }
    function wireOpen(el, where) {
      el.querySelectorAll('[data-lead-offer-open]').forEach(function (b) {
        b.addEventListener('click', function () { window.LeadOffer.open(where); });
      });
    }
    function mountSurfaces() {
      if (!window.LeadOffer.available()) return;
      var l = read('localStorage', LS_KEY);
      var cooldown = cfg.rules.cooldownDays * DAY;

      // 1. PDP mobile sticky bar: 32px strip docked on the bar's top edge (design 1b).
      var bar = document.querySelector('[data-pdp-sticky-bar]');
      if (bar && !bar.querySelector('[data-lead-offer-surface="sticky_strip"]') &&
          !(l.stripClosedAt && Date.now() - l.stripClosedAt < cooldown)) {
        var strip = fromTemplate('lead-offer-tpl-strip');
        if (strip) {
          wireOpen(strip, 'sticky_strip');
          var x = strip.querySelector('[data-lead-offer-strip-close]');
          if (x) x.addEventListener('click', function () {
            var l3 = read('localStorage', LS_KEY); l3.stripClosedAt = Date.now(); write('localStorage', LS_KEY, l3);
            strip.remove();
            track('surface_dismiss', { placement: 'sticky_strip' });
          });
          bar.insertBefore(strip, bar.firstChild);
          track('surface_view', { placement: 'sticky_strip' });
        }
      }

      // 2. Cart drawer: one line above checkout, only for a 1-item cart with no
      //    code applied. With 2+ items Bundle & Save applies and the welcome code
      //    is not honoured (DISCOUNT_CODE_NOT_HONOURED), so don't promise it.
      var drawerLine = null;
      var lastCart = null;
      var ensureDrawerLine = function () {
        var buttons = document.querySelector('.cart-drawer-v2__checkout-buttons');
        if (!buttons) return null;
        if (!drawerLine) { drawerLine = fromTemplate('lead-offer-tpl-drawer'); if (drawerLine) wireOpen(drawerLine, 'cart_drawer'); }
        if (drawerLine && drawerLine.parentNode !== buttons.parentNode) buttons.parentNode.insertBefore(drawerLine, buttons);
        return drawerLine;
      };
      var applyCart = function (cart) {
        if (cart) lastCart = cart;
        var line = ensureDrawerLine();
        if (!line || !cart) return;
        var eligible = LeadOfferRules.drawerEligible(cart) && window.LeadOffer.available();
        line.hidden = !eligible;
      };
      if (document.querySelector('.cart-drawer-v2__checkout-buttons')) {
        fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(applyCart).catch(function () {});
        document.addEventListener('cart:updated', function (e) {
          var d = e && e.detail; var cart = d && d.cart ? d.cart : d;
          if (cart && typeof cart.item_count === 'number') applyCart(cart);
          else fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(applyCart).catch(function () {});
        });
        var drawer = document.querySelector('.cart-drawer-v2__checkout-buttons').closest('aside, .cart-drawer-v2') || document.body;
        new MutationObserver(function () {
          // Re-insert after a drawer re-render, and re-apply the latest cart at
          // once so a stale "visible" line can't flash before cart:updated.
          if (drawerLine && !drawerLine.isConnected) { drawerLine.hidden = true; ensureDrawerLine(); if (lastCart) applyCart(lastCart); }
        }).observe(drawer, { childList: true, subtree: true });
      }

      // 3. Desktop header ticker: one item added to each copy of the marquee
      //    track (it loops on translateX(-50%)): after the last ORIGINAL item,
      //    and a hidden clone at the end. Hidden <1024px (CSS). Pointer-only:
      //    the track moves, so a focusable button could take keyboard focus
      //    while scrolled out of view; keyboard users have the dialog, the
      //    drawer line and the footer form.
      var track_ = document.querySelector('.geo-announcement__track');
      if (track_ && !track_.querySelector('[data-lead-offer-surface="header_ticker"]')) {
        var originals = [].filter.call(track_.querySelectorAll('.geo-announcement__item'), function (i) { return i.getAttribute('aria-hidden') !== 'true'; });
        var a = fromTemplate('lead-offer-tpl-ticker'), b = fromTemplate('lead-offer-tpl-ticker');
        if (originals.length && a && b) {
          [a, b].forEach(function (li) {
            li.querySelectorAll('button').forEach(function (btn) { btn.tabIndex = -1; });
            wireOpen(li, 'header_ticker');
          });
          b.setAttribute('aria-hidden', 'true');
          var lastOriginal = originals[originals.length - 1];
          track_.insertBefore(a, lastOriginal.nextSibling);
          track_.appendChild(b);
        }
      }
    }

    // Returning from the native post?
    var pending = read('sessionStorage', 'lp_offer_pending');
    var hasErrors = !!root.querySelector('[data-lead-offer-errors]');
    var outcome = LeadOfferRules.afterReload(pending, posted, hasErrors, Date.now());
    if (outcome !== 'none') {
      try { window.sessionStorage.removeItem('lp_offer_pending'); } catch (e) { /* noop */ }
      placement = pending.placement || 'auto';
      // The post comes back to `#contact_form`, which jumps to the first
      // newsletter form on the page (usually the footer). Put the shopper back.
      var y = +pending.y || 0;
      // Re-apply after load too: the browser's own post-load scrolling (the cleared
      // #lead-offer-form fragment, late layout) can move the page after the first restore.
      // Stop as soon as the shopper scrolls themselves.
      var userMoved = false;
      ['wheel', 'touchstart', 'keydown'].forEach(function (ev) {
        window.addEventListener(ev, function () { userMoved = true; }, { once: true, passive: true });
      });
      var restore = function () { if (!userMoved) window.scrollTo(0, y); };
      restore();
      window.addEventListener('load', function () {
        restore();
        setTimeout(restore, 250);
        setTimeout(restore, 1000);
      }, { once: true });
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      if (outcome === 'success') {
        var l2 = read('localStorage', LS_KEY);
        l2.subscribed = true;
        write('localStorage', LS_KEY, l2);
      }
      root.setAttribute('data-state', outcome);
      show('after-submit', outcome);
      track(outcome === 'success' ? 'signup_success' : 'signup_error', { placement: placement });
      return; // no auto-open logic on this page view
    }

    mountSurfaces();


    document.addEventListener('cart:added', function () {
      var s = read('sessionStorage', SS_KEY);
      s.addedToCartThisSession = true;
      write('sessionStorage', SS_KEY, s);
    });

    window.addEventListener('scroll', function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0) maxScroll = Math.max(maxScroll, window.scrollY / h);
    }, { passive: true });

    if (desktop) {
      document.addEventListener('mouseout', function (e) {
        // Only a gesture made after the exit-intent delay counts; one at 14s
        // must not fire the dialog at 20s while the shopper is back reading.
        if (!e.relatedTarget && e.clientY <= 0 && (Date.now() - start) / 1000 >= cfg.rules.exitIntentSeconds) exitIntent = true;
      });
    }

    var timer = window.setInterval(function () {
      var s = read('sessionStorage', SS_KEY);
      var l = read('localStorage', LS_KEY);
      var verdict = LeadOfferRules.decide(
        { enabled: !!cfg.enabled || !!s.simulateEnabled, forced: !!s.forced, serverSuppressed: !!cfg.serverSuppressed, autoOpenAllowed: !!cfg.autoOpenAllowed },
        {
          subscribed: !!l.subscribed, closedAt: l.closedAt || 0,
          shownThisSession: !!s.shownThisSession, dismissedThisSession: !!s.dismissedThisSession,
          addedToCartThisSession: !!s.addedToCartThisSession, fromEmail: !!s.fromEmail,
          pageviews: s.pageviews || 1
        },
        { now: Date.now(), seconds: (Date.now() - start) / 1000, scroll: maxScroll, exitIntent: exitIntent, desktop: desktop },
        cfg.rules
      );
      if (verdict.open) {
        window.clearInterval(timer);
        placement = 'auto';
        root.setAttribute('data-state', 'form');
        show(verdict.reason);
      } else if (!/^(waiting|too-early|forced-wait)$/.test(verdict.reason)) {
        window.clearInterval(timer); // a blocking rule: nothing will change on this page view
      }
    }, 1000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
